import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { Destroyer } from 'src/app/core/abstract/destroyer';
import { StringTMap } from 'src/app/core/models/common.models';
import { DBTag } from 'src/app/core/models/tags.models';
import { EntityFactory } from 'src/app/core/store/services/entity.factory';
import { TagsStoreService } from 'src/app/core/store/services/tags-store.service';
import { filterTagsByTitle } from 'src/app/shared/components/craft-tags/tags.utils';

@Component({
  selector: 'craft-tags',
  templateUrl: './craft-tags.component.html',
  styleUrls: ['./craft-tags.component.scss'],
  standalone: false,
})
export class CraftTagsComponent extends Destroyer implements OnChanges, OnDestroy {
  @Input() public focus = false;
  @Input() public disabled = false;
  @Input() public ids: readonly string[] = [];
  @Input() public placeholder = '+ Add Label';

  @Output() public selectTag = new EventEmitter<DBTag>();
  @Output() public deleteTag = new EventEmitter<DBTag>();

  @ViewChild('tagInput', { static: true }) public taginputRef: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  public tagsMap: StringTMap<DBTag> = {};
  public readonly autocompleteTags$: Observable<readonly DBTag[]>;
  public readonly inputChanged$ = new BehaviorSubject<string>('');
  public readonly idsChanged$ = new BehaviorSubject<readonly string[]>([]);
  public readonly separatorKeysCodes = [ENTER, COMMA] as const;

  constructor(
    private entityFactory: EntityFactory, //
    private tagsStore: TagsStoreService,
  ) {
    super();

    this.autocompleteTags$ = combineLatest([
      this.tagsStore.list$, //
      this.inputChanged$.pipe(distinctUntilChanged()),
      this.idsChanged$.pipe(
        map((ids) => {
          const titles = ids.map((id) => this.tagsMap[id]?.title.toLocaleLowerCase() || '');
          return new Set(titles);
        }),
      ),
    ]).pipe(
      map(([tagsList, searchText, tagsTitleSet]) => {
        return filterTagsByTitle<DBTag>(tagsList, tagsTitleSet, searchText);
      }),
      takeUntil(this.destroy$),
    );

    this.tagsStore.map$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.tagsMap = tags;
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const ids = changes.ids?.currentValue;
    const isFocused: boolean | undefined = changes.focus?.currentValue;
    if (Array.isArray(ids)) {
      this.idsChanged$.next(ids);
    }

    if (typeof isFocused === 'boolean') {
      setTimeout(() => {
        if (isFocused) {
          this.taginputRef?.nativeElement?.focus?.();
        } else {
          this.taginputRef?.nativeElement?.blur?.();
          this.autocomplete?.closePanel();
        }
      }, 0);
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.idsChanged$.complete();
    this.inputChanged$.complete();
  }

  public add(event: MatChipInputEvent, tagInput: HTMLInputElement): void {
    tagInput.value = '';

    const title = (event.value || '').trim();
    if (!title) return;

    const id = this.findTagIdByTitle(title);

    if (!id) {
      this.createTag(title);
    } else if (!this.ids.includes(id)) {
      this.selectTag.next(this.tagsMap[id]);
    }
  }

  public remove(id: string) {
    this.deleteTag.next(this.tagsMap[id]);
  }

  public selectAutocompleteTag(event: MatAutocompleteSelectedEvent, tagInput: HTMLInputElement): void {
    tagInput.value = '';
    this.selectTag.next(event.option.value);
  }

  public displayName(tag: DBTag): string {
    return tag && tag.title ? tag.title : '';
  }

  public identifyTags(index: number, item?: DBTag): string | number {
    return item?.id || index;
  }

  private findTagIdByTitle(title: string) {
    title = title.toLocaleLowerCase();
    return Object.keys(this.tagsMap).find((id) => this.tagsMap[id].title.toLocaleLowerCase() === title);
  }

  private createTag(title: string) {
    this.entityFactory
      .createTag(title)
      .pipe(
        switchMap((tag) => {
          return this.tagsStore.add(tag);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((tag) => {
        this.selectTag.next(tag);
      });
  }
}
